import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { FaCheckCircle, FaEnvelope, FaEye, FaEyeSlash, FaGlobe, FaLock, FaMapMarkerAlt, FaPhone, FaUser } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_URL;

function IndividualRegister({ formData: parentFormData, handleChange: parentHandleChange, errors: parentErrors }) {
  const [localData, setLocalData] = useState({
    accountType: 'individual',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    address: '',
    country: 'lk',
    plan: 'trial',
  });
  
  const [localErrors, setLocalErrors] = useState({});
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [canEditEmail, setCanEditEmail] = useState(true);
  const [countries, setCountries] = useState([]);
  const navigate = useNavigate();

  const formData = parentFormData || localData;

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${BASE_URL}/get-countries`);
        if (!response.ok) {
          throw new Error('Failed to fetch countries');
        }
        const data = await response.json();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
        // You may want to set some error state here
      }
    };

    fetchCountries();
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'emailAddress' && isEmailVerified) {
      return;
    }

    if (name === 'phoneNumber') {
      const country = countries.find(c => c.value === formData.country);
      const phoneCode = country?.phoneCode || '';
      let phoneNumberOnly = value;
      if (phoneCode && value.startsWith(phoneCode)) {
        phoneNumberOnly = value.substring(phoneCode.length);
      }
      
      if (parentHandleChange) {
        const completePhoneEvent = {
          target: { name: 'phoneNumber', value: phoneCode + phoneNumberOnly }
        };
        parentHandleChange(completePhoneEvent);
      } else {
        setLocalData(prev => ({
          ...prev,
          phoneNumber: phoneCode + phoneNumberOnly
        }));
      }
      return;
    }

    if (parentHandleChange) {
      parentHandleChange(e);
    } else {
      setLocalData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'emailAddress' && value !== formData.emailAddress) {
      setIsEmailVerified(false);
      setShowOtpInput(false);
      setOtp('');
      setOtpError('');
    }

    if (name === 'country') {
      const newCountry = countries.find(c => c.value === value);
      const oldCountry = countries.find(c => c.value === formData.country);
      const currentPhone = formData.phoneNumber || '';
      
      let phoneWithoutCode = currentPhone;
      if (oldCountry?.phoneCode && currentPhone.startsWith(oldCountry.phoneCode)) {
        phoneWithoutCode = currentPhone.substring(oldCountry.phoneCode.length);
      }
      
      const newPhone = (newCountry?.phoneCode || '') + phoneWithoutCode;
      
      if (parentHandleChange) {
        const phoneEvent = {
          target: { name: 'phoneNumber', value: newPhone }
        };
        parentHandleChange(phoneEvent);
      } else {
        setLocalData(prev => ({
          ...prev,
          phoneNumber: newPhone
        }));
      }
    }
  };

  const getPhoneNumberForDisplay = () => {
    const country = countries.find(c => c.value === formData.country);
    const phoneCode = country?.phoneCode || '';
    const phoneNumber = formData.phoneNumber || '';
    
    if (phoneCode && phoneNumber.startsWith(phoneCode)) {
      return phoneNumber.substring(phoneCode.length);
    }
    return phoneNumber;
  };

  const handleEmailEdit = () => {
    setIsEmailVerified(false);
    setCanEditEmail(true);
    setShowOtpInput(false);
    setOtp('');
    setOtpError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }
    
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }
    
    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (formData.phoneNumber.trim().length < 8) {
      newErrors.phoneNumber = 'Phone number must be at least 8 digits';
    }
    
    if (!formData.emailAddress?.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.emailAddress.trim())) {
        newErrors.emailAddress = 'Invalid email format';
      }
    }
    
    if (!formData.address?.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.trim().length < 5) {
      newErrors.address = 'Address must be at least 5 characters';
    }
    
    if (!formData.country?.trim()) {
      newErrors.country = 'Country is required';
    }
    
    if (!formData.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else {
      const hasUppercase = /[A-Z]/.test(formData.password);
      const hasLowercase = /[a-z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
      
      if (!(hasUppercase && hasLowercase && hasNumber && hasSpecial)) {
        newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
      }
    }
    
    if (!formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!isEmailVerified && formData.emailAddress?.trim()) {
      newErrors.emailAddress = 'Please verify your email first';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOTP = async () => {
    try {
      setErrors({});
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.emailAddress?.trim()) {
        setErrors(prev => ({
          ...prev,
          emailAddress: 'Email address is required'
        }));
        setMessage('Please enter an email address');
        return;
      }
      
      if (!emailRegex.test(formData.emailAddress.trim())) {
        setErrors(prev => ({
          ...prev,
          emailAddress: 'Please enter a valid email address'
        }));
        setMessage('Please enter a valid email address');
        return;
      }

      setLoading(true);
      const response = await fetch(`${BASE_URL}/send-verification-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emailAddress: formData.emailAddress.trim() })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage('OTP sent to your email');
        setShowOtpInput(true);
      } else {
        throw new Error(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      setMessage(`Failed to send OTP: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      if (!otp || otp.length !== 6) {
        setOtpError('Please enter a valid 6-digit OTP');
        setMessage('Please enter a valid 6-digit OTP');
        return;
      }

      setLoading(true);
      const response = await fetch(`${BASE_URL}/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emailAddress: formData.emailAddress,
          otp
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setIsEmailVerified(true);
        setShowOtpInput(false);
        setMessage('Email verified successfully');
        setOtpError('');
        setCanEditEmail(false);
      } else {
        const errorMsg = data.error || 'Invalid OTP';
        setOtpError(errorMsg);
        setMessage(errorMsg);
      }
    } catch (error) {
      setOtpError(error.message);
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    setErrors({});

    if (!validateForm()) {
      setMessage('Please fill in all required fields correctly');
      return;
    }

    if (!isEmailVerified) {
      setErrors(prev => ({
        ...prev,
        emailAddress: 'Please verify your email first'
      }));
      setMessage('Please verify your email first');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/register-individual`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          accountType: 'individual'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      setMessage("Registration successful!");

      setTimeout(() => {
        navigate("/LoginTabs");         
      }, 1500);
      
      if (!parentHandleChange) {
        setLocalData({
          accountType: 'individual',
          firstName: '',
          lastName: '',          
          emailAddress: '',
          password: '',
          confirmPassword: '',
          address: '',
          phoneNumber: '',
          country: 'lk',
          plan: 'trial',
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setLoading(false);
      setMessage(`${error.message || "Failed to register. Please try again."}`);
    }
  };

  return (
    <Card className="shadow-lg border-0 rounded-lg mb-4">
      <Card.Header className="bg-primary text-white text-center py-3">
        <h3 className="mb-0">Individual Registration</h3>
        <p className="text-white-50 mb-0">Create your personal account</p>
      </Card.Header>
      <Card.Body className="p-4">
       {message && (
         <Alert 
           variant={
             message === "Registration successful!" || 
             message === "OTP sent to your email" || 
             message === "Email verified successfully" 
             ? "success" : "danger"
           } 
           className="mb-4" 
           dismissible
           onClose={() => setMessage('')}
         >
           {message}
         </Alert>
       )}
        
        <Form onSubmit={handleSubmit} noValidate>
          <h5 className="border-bottom pb-2 mb-3">Personal Information</h5>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>First Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaUser /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    placeholder="Enter first name"
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Last Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaUser /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    isInvalid={!!errors.lastName}
                    placeholder="Enter last name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Country</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaGlobe /></InputGroup.Text>
                  <Form.Select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    isInvalid={!!errors.country}
                  >
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.country}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Phone Number</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaPhone />
                  </InputGroup.Text>
                  <InputGroup.Text className="bg-light">
                    {countries.find(c => c.value === formData.country)?.phoneCode || '+'}
                  </InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="phoneNumber"
                    value={getPhoneNumberForDisplay()}
                    onChange={handleChange}
                    isInvalid={!!errors.phoneNumber}
                    placeholder="Enter phone number"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phoneNumber}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text className="text-muted">
                  Enter your phone number without the country code
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <h5 className="border-bottom pb-2 mb-3 mt-4">Contact Information</h5>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email Address</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleChange}
                    isInvalid={!!errors.emailAddress}
                    placeholder="Enter email address"
                    disabled={!canEditEmail}
                  />
                  {isEmailVerified ? (
                    <Button
                      variant="outline-secondary"
                      onClick={handleEmailEdit}
                    >
                      Edit Email
                    </Button>
                  ) : (
                    <Button
                      variant={isEmailVerified ? "success" : "primary"}
                      onClick={handleSendOTP}
                      disabled={loading || !formData.emailAddress}
                    >
                      {isEmailVerified ? "Verified" : "Verify Email"}
                    </Button>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.emailAddress}
                  </Form.Control.Feedback>
                </InputGroup>
                {isEmailVerified && (
                  <Form.Text className="text-success">
                    <FaCheckCircle className="me-1" />
                    Email verified
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Address</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    isInvalid={!!errors.address}
                    placeholder="Enter your address"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {showOtpInput && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Enter OTP</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaLock /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      isInvalid={!!otpError}
                      placeholder="Enter OTP"
                      maxLength={6}
                    />
                    <Button
                      variant="primary"
                      onClick={handleVerifyOTP}
                      disabled={loading || otp.length !== 6}
                    >
                      Verify OTP
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {otpError}
                    </Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Check your email for the OTP
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          <h5 className="border-bottom pb-2 mb-3 mt-4">Security</h5>
          
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaLock /></InputGroup.Text>
                  <Form.Control
                    type={showPassword ? "text" : "password"} 
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    isInvalid={!!errors.password}
                    placeholder="Create password"
                  />
                  <button
                  type="button"
                  className="input-group-text"
                  onClick={togglePasswordVisibility}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text className="text-muted">
                  Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Confirm Password</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaLock /></InputGroup.Text>
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"} 
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    isInvalid={!!errors.confirmPassword}
                    placeholder="Confirm password"
                  />
                   <button
                  type="button"
                  className="input-group-text"
                  onClick={toggleConfirmPasswordVisibility}
                  style={{ cursor: 'pointer' }}
                >
                  {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <input type="hidden" name="plan" value={formData.plan || 'trial'} />

          <div className="d-grid gap-2">
            <Button type="submit" variant="primary" size="lg" disabled={loading}>
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </div>
          
          <div className="mt-3 d-none">
            <pre>{JSON.stringify(formData, null, 2)}</pre>
          </div>
        </Form>
      </Card.Body>
      <Card.Footer className="text-center py-3 bg-light">
        <p className="mb-0">
          Already have an account? <a href="/LoginTabs" className="fw-bold">Log in</a>
        </p>
      </Card.Footer>
    </Card>
  );
}

export default IndividualRegister;